import { Component, Fragment } from 'react';
import { RoutableProps } from 'preact-router';
import { BrowserView, isDesktop, isMobile } from 'react-device-detect';
import throttle from 'lodash.throttle';
import debounce from 'lodash.debounce';

import tlog from './lib/tlog';
import { capitalize } from './lib/string';

import LinkButton from './components/link_button';
import Tile from './components/tile';
import Profile from './components/profile';
import Separator from './components/separator';

import classNames from 'classnames';
import classes from './app.less';
import { DisplayText, Post, User } from './services/type';
import { API } from './services/api';
import PageDescription from './components/page_description';

type State = {
  isLoading: boolean;
  hasMore: boolean;
  showNavBar: boolean;
  user?: User;
  posts: Post[];
};

type Props = { username?: string } & RoutableProps;

export default class App extends Component<Props, State> {
  state: State = {
    isLoading: false,
    hasMore: true,
    showNavBar: isMobile,
    user: undefined,
    posts: [],
  };

  componentDidMount = async () => {
    window.addEventListener('scroll', this.handleScroll);

    const { username } = this.props;
    if (username) {
      const [user, posts] = await Promise.all([
        API.fetchUser(username),
        API.fetchPosts(username),
      ]);
      this.setState({
        isLoading: false,
        user,
        posts: posts || [],
      });
    }
  };

  componentDidCatch(err: Error) {
    tlog.error(err);
  }

  root: HTMLDivElement | null = null;

  handleRef = (ref: HTMLDivElement | null) => {
    this.root = ref;
  };

  loadMore = debounce(async () => {
    const { username } = this.props;
    const { posts } = this.state;

    if (username && posts && posts.length > 0) {
      this.setState({ isLoading: true });

      const newPosts = await API.fetchPosts(
        username,
        new Date(posts[posts.length - 1].created).toISOString(),
      );
      if (newPosts && newPosts.length > 0) {
        this.setState({
          isLoading: false,
          posts: mergePosts(posts, newPosts),
        });
      } else {
        this.setState({
          isLoading: false,
          hasMore: false,
        });
      }
    }
  }, 100);

  handleScroll = throttle(() => {
    if (isDesktop) {
      const isShown = window.scrollY > 170;
      if (this.state.showNavBar !== isShown) {
        this.setState({ showNavBar: isShown });
      }
    }

    if (!this.root) {
      return;
    }
    // no more posts to browse
    if (!this.state.hasMore) {
      return;
    }
    if (this.state.isLoading) {
      return;
    }
    // check if reaching bottom of page
    // For Chrome, window.scrollY in production deviates from one in development by 0.5 pixel
    // We need to compensate for this to make sure load_more() is triggered when the scrollbar is at the bottom
    if (
      (window.innerHeight + window.scrollY) * 1.01 >=
      this.root.clientHeight
    ) {
      this.loadMore();
    }
  }, 200);

  handleClick = async (postId: string) => {
    const p = this.state.posts.find((x) => x.id === postId);
    if (p) {
      await API.clickPost(this.props.username || '', p);
    }
  };

  classNameByNetwork(network: string) {
    return network === 'tiktok' ? classes.tiktok : classes.default;
  }

  renderNav(user: User, size: 'small' | 'large') {
    const { showNavBar } = this.state;
    const navClasses = classNames({
      [classes[`nav${capitalize(size)}`]]: true,
      [classes.hidden]: !showNavBar && size === 'small',
      [this.classNameByNetwork(window.TA.networkName)]: true,
    });
    return (
      <Fragment>
        <div className={navClasses}>
          <Profile
            size={size}
            username={user.username}
            name={user.name}
            pictureUrl={user.pictureUrl}
          />
          <LinkButton label={user.visitLabel} url={user.visitUrl} />
        </div>
        <PageDescription
          className={!showNavBar && size === 'small' ? classes.hidden : ''}
          size={size}
          description={user.pageDescription}
        />
      </Fragment>
    );
  }

  render() {
    const { isLoading, user, posts } = this.state;
    const showDisplayText =
      !!user?.displayTextType && user?.displayTextType !== DisplayText.None;
    return (
      <div ref={this.handleRef}>
        <div
          className={classNames(
            classes.header,
            user?.pageDescription && classes.headerWithDesc,
          )}
        >
          {user && this.renderNav(user, 'small')}
        </div>

        <div
          className={classNames(
            classes.main,
            !showDisplayText && classes.mainWithoutDisplayText,
            this.classNameByNetwork(window.TA.networkName),
          )}
        >
          {user && (
            <BrowserView className={classes.navWrapper}>
              {this.renderNav(user, 'large')}
              <Separator />
            </BrowserView>
          )}
          <div className={classes.tiles}>
            {posts.map((p, i) => (
              <Tile
                key={i}
                postId={p.id}
                mediaType={p.type}
                imageUrl={p.imageUrl}
                smartlink={p.smartlink}
                displayText={p.displayText}
                onClick={this.handleClick}
                showDisplayText={showDisplayText}
              />
            ))}
          </div>
          {isLoading && posts.length > 0 && (
            <div className={classes.loading}>Loading...</div>
          )}
        </div>
      </div>
    );
  }
}

function mergePosts(p1: Post[], p2: Post[] = []): Post[] {
  return p1.concat(
    p2.filter((x) => p1.findIndex((y) => x.created === y.created) === -1),
  );
}
